import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_TOKEN } from 'constants/AuthConstant';
import AuthService from 'services/AuthService';
import { getUserInfo } from 'services/api';
import { getDefaultAuthenticatedPath } from 'utils/authRedirect'; 

export const initialState = {
	loading: false,
	message: '',
	showMessage: false,
	redirect: '',
	token: '',
	user: null
}

export const signIn = createAsyncThunk('auth/signIn', async ({ email, username, password, isAdmin = false }, thunkAPI) => {
	const { dispatch, rejectWithValue } = thunkAPI;
	
	try {
		const response = isAdmin 
			? await AuthService.adminLogin({ username, password })
			: await AuthService.login({ email, password });
		const token = response.token;

		if (!token) throw new Error('Token not found in response');

		localStorage.setItem(AUTH_TOKEN, token);

		if (isAdmin) {
			const { username, role, title, name, email} = response;  
			const user = { username, name, role, title, email };
            dispatch(signInSuccess({ token, user }));
            return { token, user, redirect: '/admin/dashboard' };
		}

		const userInfo = await getUserInfo();

		const { default: productReducer, fetchProducts } = await import('./productSlice');
		const { fetchAlexaProducts } = await import('./alexaSlice');
		const { fetchOnePageProducts } = await import('./onePageSlice');
		const { fetchMobileApps } = await import('./mobileAppSlice');
		const { injectReducer } = await import('store');

		injectReducer('products', productReducer);
		await dispatch(fetchProducts());
		await dispatch(fetchAlexaProducts());
		await dispatch(fetchOnePageProducts());
		await dispatch(fetchMobileApps());



		dispatch(signInSuccess({ token, user: userInfo }));
		return { token, user: userInfo };
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Login failed');
	}
});

export const signOut = createAsyncThunk('auth/signOut', async () => {
    try {
        await AuthService.logout();

        return {}; 
    } catch (error) {
        console.error("Error during sign-out:", error);
        throw error;
    }
});


export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		authenticated: (state, action) => {
			state.loading = false;
			state.redirect = getDefaultAuthenticatedPath(action.payload.user);
			state.token = action.payload.token;
			state.user = action.payload.user;
			state.showMessage = false;
			state.message = '';	
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload
			state.showMessage = true
			state.loading = false
		},
		hideAuthMessage: (state) => {
			state.message = ''
			state.showMessage = false
		},
		signOutSuccess: (state) => {
			state.loading = false;
			state.token = null;
			state.redirect = '/login';
			state.user = null;
		},
		showLoading: (state, action) => {
			state.loading = action.payload;
		  },		  
		signInSuccess: (state, action) => {
			state.loading = false;
			state.redirect = action.payload.redirect || getDefaultAuthenticatedPath(action.payload.user);
			state.token = action.payload.token;
			state.user = action.payload.user;
			state.showMessage = false;
			state.message = '';	
		},
		clearRedirect: (state) => {
            state.redirect = null;
        }
	},
	extraReducers: (builder) => {
		builder
			.addCase(signIn.pending, (state) => {
				state.loading = true
			})
			.addCase(signIn.fulfilled, (state, action) => {
				state.loading = false;
				state.redirect = action.payload.redirect || getDefaultAuthenticatedPath(action.payload.user);
				state.token = action.payload.token;
				state.user = action.payload.user;
				state.showMessage = false;
        		state.message = '';				
			})
			
			.addCase(signIn.rejected, (state, action) => {
				state.loading = false;
				state.message = action.payload;
				state.showMessage = true;
				state.token = null;
				state.user = null;
			})
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false;
				state.token = null;
				state.user = null;
				state.redirect = '/';
			})
			.addCase(signOut.rejected, (state) => {
				state.loading = false
				state.token = null
				state.user = null
				state.redirect = '/'
			})
	},
})

export const { 
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess,
	clearRedirect
} = authSlice.actions

export default authSlice.reducer
